'use client'

import { ArrowDownToLine, Bell, Check, CheckCheck, CreditCard, RefreshCw, ShieldAlert } from 'lucide-react'
import Link from 'next/link'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { trpc } from '@/lib/trpc'

const TYPE_CONFIG = {
	SECURITY_ALERT: { icon: ShieldAlert, color: 'text-red-500' },
	UPDATE_AVAILABLE: { icon: ArrowDownToLine, color: 'text-blue-500' },
	RISK_CHANGE: { icon: RefreshCw, color: 'text-yellow-500' },
	SYNC_COMPLETE: { icon: Check, color: 'text-green-500' },
	PAYMENT_FAILED: { icon: CreditCard, color: 'text-amber-500' },
} as const

export function NotificationBell() {
	const [open, setOpen] = useState(false)
	const utils = trpc.useUtils()

	const { data: unreadCount = 0 } = trpc.notification.getUnreadCount.useQuery(undefined, {
		refetchInterval: 60000, // Poll every minute
	})

	const { data: notificationsData } = trpc.notification.list.useQuery({ take: 10 }, { enabled: open })

	const markRead = trpc.notification.markRead.useMutation({
		onSuccess: () => {
			utils.notification.getUnreadCount.invalidate()
			utils.notification.list.invalidate()
		},
	})

	const markAllRead = trpc.notification.markAllRead.useMutation({
		onSuccess: () => {
			utils.notification.getUnreadCount.invalidate()
			utils.notification.list.invalidate()
		},
	})

	const notifications = notificationsData?.notifications ?? []

	const formatTime = (date: Date | string) => {
		const d = new Date(date)
		const now = new Date()
		const diffMs = now.getTime() - d.getTime()
		const diffMins = Math.floor(diffMs / 60000)
		const diffHours = Math.floor(diffMs / 3600000)
		const diffDays = Math.floor(diffMs / 86400000)

		if (diffMins < 1) return 'now'
		if (diffMins < 60) return `${diffMins}m`
		if (diffHours < 24) return `${diffHours}h`
		if (diffDays < 7) return `${diffDays}d`
		return d.toLocaleDateString()
	}

	return (
		<DropdownMenu open={open} onOpenChange={setOpen}>
			<DropdownMenuTrigger asChild>
				<Button variant="ghost" size="icon" className="relative">
					<Bell className="h-5 w-5" />
					{unreadCount > 0 && (
						<span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground">
							{unreadCount > 99 ? '99+' : unreadCount}
						</span>
					)}
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent className="w-80" align="end" forceMount>
				<DropdownMenuLabel className="flex items-center justify-between">
					<span>Notifications</span>
					{unreadCount > 0 && (
						<Button
							variant="ghost"
							size="sm"
							className="h-auto p-1 text-xs"
							onClick={() => markAllRead.mutate()}
							disabled={markAllRead.isPending}
						>
							<CheckCheck className="h-3 w-3 mr-1" />
							Mark all read
						</Button>
					)}
				</DropdownMenuLabel>
				<DropdownMenuSeparator />

				{notifications.length === 0 ? (
					<div className="p-4 text-center text-sm text-muted-foreground">No notifications</div>
				) : (
					<div className="max-h-80 overflow-y-auto">
						{notifications.map((notification) => {
							const typeConfig =
								TYPE_CONFIG[notification.type as keyof typeof TYPE_CONFIG] ?? TYPE_CONFIG.SYNC_COMPLETE
							const Icon = typeConfig.icon

							return (
								<button
									key={notification.id}
									type="button"
									className={`w-full flex items-start gap-3 p-3 text-left hover:bg-accent/50 transition-colors ${
										!notification.isRead ? 'bg-accent/20' : ''
									}`}
									onClick={() => {
										if (!notification.isRead) {
											markRead.mutate({ id: notification.id })
										}
									}}
								>
									<Icon className={`h-4 w-4 mt-0.5 shrink-0 ${typeConfig.color}`} />
									<div className="min-w-0 flex-1">
										<div className="flex items-center justify-between gap-2">
											<span className="text-sm font-medium truncate">{notification.title}</span>
											<span className="text-xs text-muted-foreground shrink-0">
												{formatTime(notification.createdAt)}
											</span>
										</div>
										<p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
											{notification.message}
										</p>
									</div>
									{!notification.isRead && (
										<div className="h-2 w-2 rounded-full bg-primary shrink-0 mt-1.5" />
									)}
								</button>
							)
						})}
					</div>
				)}

				{/* Always reachable, regardless of count — the full-text page is where
				    long messages (truncated above) can actually be read. */}
				<DropdownMenuSeparator />
				<div className="p-2 text-center">
					<Button variant="ghost" size="sm" className="text-xs" asChild>
						<Link href="/notifications" onClick={() => setOpen(false)}>
							View all notifications
							{notificationsData && notificationsData.total > 0 ? ` (${notificationsData.total})` : ''}
						</Link>
					</Button>
				</div>
			</DropdownMenuContent>
		</DropdownMenu>
	)
}
